import { Head, Link } from "@inertiajs/react"
import {
  Building2,
  Calendar,
  Eye,
  Footprints,
  Globe,
  MapPin,
  MessageSquare,
  Pencil,
  Plus,
  Ruler,
  Users,
  Weight,
} from "lucide-react"
import { useState } from "react"

import ContentActionMenu from "@/components/content-action-menu"
import { typedPost } from "@/lib/typed-router"
import type { StartConversation } from "@/types/router-mutations"
import FollowButton from "@/components/players/follow-button"
import HighlightsGrid from "@/components/players/highlights-grid"
import PlayerBadges from "@/components/players/player-badges"
import StatsTable from "@/components/players/stats-table"
import ShareButton from "@/components/share-button"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import AppLayout from "@/layouts/app-layout"
import type PlayersShow from "@/types/serializers/PlayersShow"

const POSITION_LABELS: Record<string, string> = {
  portero: "Portero",
  defensa_central: "Defensa Central",
  lateral_derecho: "Lateral Derecho",
  lateral_izquierdo: "Lateral Izquierdo",
  mediocampista_central: "Mediocampista Central",
  mediocampista_ofensivo: "Mediocampista Ofensivo",
  mediocampista_defensivo: "Mediocampista Defensivo",
  extremo_derecho: "Extremo Derecho",
  extremo_izquierdo: "Extremo Izquierdo",
  delantero_centro: "Delantero Centro",
  segundo_delantero: "Segundo Delantero",
}

const FOOT_LABELS: Record<string, string> = {
  derecho: "Derecho",
  izquierdo: "Izquierdo",
  ambidiestro: "Ambidiestro",
}

const AVAILABILITY_LABELS: Record<
  string,
  { label: string; className: string }
> = {
  disponible: {
    label: "Disponible",
    className: "bg-green-500/20 text-green-400 border-green-500/30",
  },
  no_disponible: {
    label: "No disponible",
    className: "bg-krown-grey-60/30 text-muted-foreground border-krown-grey-60",
  },
  escuchando_ofertas: {
    label: "Escuchando ofertas",
    className: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  },
}

function calculateAge(dateOfBirth: string): number {
  const today = new Date()
  const birthDate = new Date(dateOfBirth)
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--
  }
  return age
}

type TabKey = "stats" | "highlights" | "about"

export default function PlayerShow({
  player,
  seasons,
  highlights,
  badges,
  badge_display_info,
  is_following,
  is_own_profile,
  is_blocked,
}: PlayersShow) {
  const [activeTab, setActiveTab] = useState<TabKey>("stats")
  const age = calculateAge(player.date_of_birth)
  const positionLabel = POSITION_LABELS[player.position] ?? player.position
  const footLabel = FOOT_LABELS[player.dominant_foot] ?? player.dominant_foot
  const availability =
    AVAILABILITY_LABELS[player.availability] ?? AVAILABILITY_LABELS.disponible

  const canonicalUrl = `https://krownft.com/jugadores/${player.slug}`
  const metaDescription = `${player.user.full_name}, ${age} años, ${positionLabel} de ${player.city}, ${player.country}.${player.bio ? ` ${player.bio}` : ""}`
  const pageTitle = `${player.user.full_name} - ${positionLabel} | KrownFT`

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: player.user.full_name,
    jobTitle: positionLabel,
    birthDate: player.date_of_birth,
    address: {
      "@type": "PostalAddress",
      addressLocality: player.city,
      addressRegion: player.region,
      addressCountry: player.country,
    },
    ...(player.height_cm ? { height: `${player.height_cm} cm` } : {}),
  }

  const tabs: { key: TabKey; label: string }[] = [
    { key: "stats", label: "Estadísticas" },
    { key: "highlights", label: "Highlights" },
    { key: "about", label: "Sobre mí" },
  ]

  return (
    <AppLayout>
      <Head title={pageTitle}>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="profile" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Head>

      <div className="bg-background min-h-svh">
        {/* Profile header */}
        <div className="mx-auto max-w-4xl px-6 pt-8 pb-6">
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
            {/* Large circular avatar with turquoise border */}
            <Avatar className="border-krown-200 h-[100px] w-[100px] shrink-0 border-3">
              <AvatarImage
                src={player.avatar_url ?? undefined}
                alt={player.user.full_name}
                className="object-cover"
              />
              <AvatarFallback className="bg-krown-600 text-2xl font-bold text-white">
                {player.user.first_name[0]}
                {player.user.last_name[0]}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 text-center sm:text-left">
              <h1 className="text-2xl font-bold text-white">
                {player.user.full_name}
              </h1>
              <p className="text-muted-foreground">@{player.user.nickname}</p>
              <p className="text-krown-200 mt-1 text-sm font-semibold tracking-wider uppercase">
                {positionLabel}
                {player.secondary_position && (
                  <span className="text-muted-foreground font-normal">
                    {" "}
                    /{" "}
                    {POSITION_LABELS[player.secondary_position] ??
                      player.secondary_position}
                  </span>
                )}
              </p>

              {(badges.length > 0 || is_own_profile) && (
                <div className="mt-2 flex justify-center sm:justify-start">
                  <PlayerBadges
                    badges={badges}
                    badgeDisplayInfo={badge_display_info}
                    isOwnProfile={is_own_profile}
                  />
                </div>
              )}

              <div className="mt-2 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                <span
                  className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${availability.className}`}
                >
                  {availability.label}
                </span>
                <span className="text-muted-foreground flex items-center gap-1 text-sm">
                  <MapPin className="text-krown-200 h-3.5 w-3.5" />
                  {player.city}, {player.country}
                </span>
                {(player.current_club || player.current_division) && (
                  <span className="text-muted-foreground flex items-center gap-1 text-sm">
                    <Building2 className="text-krown-200 h-3.5 w-3.5" />
                    {player.current_club && (
                      <span className="font-medium text-white">
                        {player.current_club}
                      </span>
                    )}
                    {player.current_club && player.current_division && (
                      <span className="mx-0.5">·</span>
                    )}
                    {player.current_division && (
                      <span>{player.current_division}</span>
                    )}
                  </span>
                )}
                {player.nationality &&
                  player.nationality !== player.country && (
                    <span className="text-muted-foreground flex items-center gap-1 text-sm">
                      <Globe className="text-krown-200 h-3.5 w-3.5" />
                      {player.nationality}
                    </span>
                  )}
              </div>

              <div className="mt-3 flex flex-wrap items-center justify-center gap-3 sm:justify-start">
                <span className="text-muted-foreground flex items-center gap-1 text-sm">
                  <Users className="text-krown-200 h-3.5 w-3.5" />
                  {player.followers_count}{" "}
                  {player.followers_count === 1 ? "seguidor" : "seguidores"}
                </span>
                <span className="text-muted-foreground text-sm">
                  {player.following_count} siguiendo
                </span>
                {player.profile_views_count > 0 && (
                  <span className="text-muted-foreground flex items-center gap-1 text-sm">
                    <Eye className="text-krown-200 h-3.5 w-3.5" />
                    {player.profile_views_count} visitas al perfil
                  </span>
                )}
                <ShareButton
                  url={canonicalUrl}
                  title={`¡Mirá el perfil de ${player.user.full_name} en KrownFT!`}
                  variant="icon"
                />
                {!is_own_profile && (
                  <>
                    <FollowButton
                      userId={player.user.id}
                      isFollowing={is_following}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-border hover:bg-card text-white"
                      onClick={() =>
                        typedPost<StartConversation>("/mensajes", {
                          receiver_id: player.user.id,
                        })
                      }
                    >
                      <MessageSquare className="mr-1.5 h-4 w-4" />
                      Enviar mensaje
                    </Button>
                    <ContentActionMenu
                      reportableType="User"
                      reportableId={player.user.id}
                      hideableType="User"
                      hideableId={player.user.id}
                      showBlock
                      blockUserId={player.user.id}
                      isBlocked={is_blocked}
                    />
                  </>
                )}
              </div>

              {is_own_profile && (
                <div className="mt-3 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-krown-400 text-krown-400 hover:bg-krown-400/10"
                    asChild
                  >
                    <Link href="/perfil/editar">
                      <Pencil className="mr-1.5 h-4 w-4" />
                      Editar perfil
                    </Link>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-border hover:bg-card text-white"
                    asChild
                  >
                    <Link href="/perfil/temporadas">
                      <Plus className="mr-1.5 h-4 w-4" />
                      Temporada
                    </Link>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-border hover:bg-card text-white"
                    asChild
                  >
                    <Link href="/perfil/highlights">
                      <Plus className="mr-1.5 h-4 w-4" />
                      Highlight
                    </Link>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="border-border bg-krown-grey-85 border-y">
          <div className="mx-auto flex max-w-4xl items-center justify-center gap-8 px-6 py-4">
            <StatItem
              icon={<Calendar className="text-krown-200 h-4 w-4" />}
              label="EDAD"
              value={String(age)}
            />
            {player.height_cm && (
              <StatItem
                icon={<Ruler className="text-krown-200 h-4 w-4" />}
                label="ALTURA"
                value={`${player.height_cm} cm`}
              />
            )}
            {player.weight_kg && (
              <StatItem
                icon={<Weight className="text-krown-200 h-4 w-4" />}
                label="PESO"
                value={`${player.weight_kg} kg`}
              />
            )}
            <StatItem
              icon={<Footprints className="text-krown-200 h-4 w-4" />}
              label="PIE"
              value={footLabel}
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="border-border border-b">
          <div className="mx-auto flex max-w-4xl px-6">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-3 text-sm font-semibold tracking-wider uppercase transition-colors ${
                  activeTab === tab.key
                    ? "border-krown-accent border-b-[3px] text-white"
                    : "text-muted-foreground hover:text-white"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        <div className="mx-auto max-w-4xl px-6 py-8">
          {activeTab === "stats" && (
            <section>
              <StatsTable seasons={seasons} isOwnProfile={is_own_profile} />
            </section>
          )}

          {activeTab === "highlights" && (
            <section>
              <HighlightsGrid
                highlights={highlights}
                playerName={player.user.full_name}
                playerSlug={player.slug}
                isOwnProfile={is_own_profile}
              />
            </section>
          )}

          {activeTab === "about" && (
            <section>
              {player.bio ? (
                <>
                  <p className="text-krown-grey-30">{player.bio}</p>
                  {player.idol && (
                    <p className="text-muted-foreground mt-3 text-sm">
                      Ídolo:{" "}
                      <span className="font-semibold text-white">
                        {player.idol}
                      </span>
                    </p>
                  )}
                </>
              ) : (
                <p className="text-muted-foreground">Sin información</p>
              )}
            </section>
          )}
        </div>
      </div>
    </AppLayout>
  )
}

function StatItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode
  label: string
  value: string
}) {
  return (
    <div className="flex flex-col items-center gap-1">
      {icon}
      <p className="text-lg font-bold text-white">{value}</p>
      <p className="text-muted-foreground text-[10px] tracking-wider uppercase">
        {label}
      </p>
    </div>
  )
}
